import clsx from 'clsx';
import PropTypes from 'prop-types';
import { memo, useCallback, useRef } from 'react';

import { DATE_TIME_SHORT, TIME_SHORT } from '@sportsbook/constants';

import { MatchType } from '../../api/types';
import { useAddSelection } from '../../hooks/use-add-selection';
import { useAsRef } from '../../hooks/use-as-ref';
import { useProviderIsActive } from '../../hooks/use-provider-is-active';
import { useTrackEvent } from '../../hooks/use-track-event';
import { SentryService } from '../../services/sentry';
import { isDateInPastOrToday } from '../../utils/date';
import { isAllowedStatus, getMainMarketOutcomeType, isSportWithDraw, ensureOutcomes } from '../../utils/markets';
import { shallowEqual } from '../../utils/shallow-equal';
import { FavoriteMatchesToggle } from '../favorite-matches-toggle/favorite-matches-toggle';
import { FormattedDateTime } from '../formatted-date-time/formatted-date-time';
import { IconSport } from '../icons/icon-sports/icon-sport';
import { LazyRenderedComponent } from '../lazy-rendered-component/lazy-rendered-component';
import { Odd } from '../odd/odd';
import { RouteLink } from '../route-link/route-link';
import { TimeOrStatus } from '../time-or-status/time-or-status';

import { MarketCardCompetitors } from './competitors/market-card-competitors';
import { customizations } from './competitors/market-card-competitors.customizations';
import { GTM_EVENTS } from './gtm';

import './market-card.scss';

export function MarketCardUI({ onCardClick, match }) {
  const {
    matchId,
    matchSlug,
    sportKey,
    sportName,
    categoryName,
    leagueName,
    nameA,
    nameB,
    logoA,
    logoB,
    totalScore,
    isLive,
    clock,
    startTime,
    stage,
    status,
    mainMarket,
    mainMarketOutcomesCount,
    provider,
  } = match;
  const { isTeamLogoHidden } = customizations.useCustomizations();

  const { trackEvent } = useTrackEvent();
  const { selectedOutcomeIds, addSelection } = useAddSelection();
  const matchRef = useAsRef(match);

  const onOddsClickCallback = useCallback(
    selectedOutcomeId => {
      const selectedOutcome = matchRef.current?.mainMarket?.outcomes.find(
        outcome => outcome.outcomeId === selectedOutcomeId
      );

      if (!selectedOutcome) return;

      addSelection({ match: matchRef.current, market: matchRef.current?.mainMarket, outcome: selectedOutcome });
    },
    [matchRef, addSelection]
  );

  const favoriteToggleRef = useRef();
  const marketOddsRef = useRef();

  const onLinkClick = useCallback(
    event => {
      const isFavoriteToggleClicked = favoriteToggleRef.current?.contains?.(event.target);
      if (onCardClick && !isFavoriteToggleClicked) {
        event.preventDefault();
        onCardClick?.({
          matchId,
          matchSlug,
          sportKey,
        });
        return;
      }

      if (isFavoriteToggleClicked) {
        event.preventDefault();
      } else if (!marketOddsRef.current?.contains(event.target)) {
        trackEvent(GTM_EVENTS.MATCH_CLICK);
      }
    },
    [trackEvent, onCardClick]
  );

  const isWithoutDraw = !isSportWithDraw(mainMarketOutcomesCount);
  const outcomesOrPlaceholders = ensureOutcomes(mainMarket?.outcomes, mainMarketOutcomesCount);
  const isProviderActive = useProviderIsActive(provider);

  if (!outcomesOrPlaceholders) {
    SentryService.captureException(
      new Error(`No outcomes in main market for match: ${matchId}. Expected ${mainMarketOutcomesCount} outcomes.`)
    );

    return null;
  }

  return (
    <RouteLink
      className={clsx('sb-MarketCard', isTeamLogoHidden && 'isTeamLogoHidden')}
      draggable={false}
      to={`/${sportKey}/${matchSlug}-m-${matchId}`}
      onClick={onLinkClick}>
      <LazyRenderedComponent className="sb-MarketCard-Loader sb-Preloader">
        <div className="sb-MarketCard-header">
          <div className="sb-MarketCard-sport">
            <IconSport
              className="sb-MarketCard-sportIcon"
              size={12}
              sportKey={sportKey}
              sportName={sportName}
              isActive
            />

            <span className="sb-MarketCard-sportName" title={`${categoryName}, ${leagueName}`}>
              {`${categoryName}, ${leagueName}`}
            </span>
          </div>

          <FavoriteMatchesToggle favoriteRef={favoriteToggleRef} id={matchId} matchStatus={status} />

          {isLive ? (
            <span className="sb-MarketCard-live">
              <span className="sb-MarketCard-liveText">
                <TimeOrStatus clock={clock} stage={stage} status={status} />
              </span>
            </span>
          ) : (
            <span className="sb-MarketCard-date">
              <FormattedDateTime
                format={isDateInPastOrToday(startTime) ? TIME_SHORT : DATE_TIME_SHORT}
                value={startTime}
              />
            </span>
          )}
        </div>
        <MarketCardCompetitors
          isLive={isLive}
          logoA={logoA}
          logoB={logoB}
          nameA={nameA}
          nameB={nameB}
          totalScore={totalScore}
        />
        <div ref={marketOddsRef} className={clsx('sb-MarketCard-odds', { isWithoutDraw })}>
          {outcomesOrPlaceholders.map((outcome, index) => (
            <Odd
              key={`${outcome.outcomeId}-${matchId}`}
              className="sb-MarketCard-odd"
              dataTestId="market-card-odd"
              isLocked={!isAllowedStatus(mainMarket?.marketStatus) || !outcome.active || !isProviderActive}
              isSelected={selectedOutcomeIds.indexOf(outcome.id) >= 0}
              labelId={`main-market-outcome.${getMainMarketOutcomeType(index, mainMarketOutcomesCount)}`}
              odds={outcome.odds}
              outcomeId={outcome.outcomeId}
              onOddsClick={onOddsClickCallback}
            />
          ))}
        </div>
      </LazyRenderedComponent>
    </RouteLink>
  );
}

MarketCardUI.propTypes = {
  match: MatchType.isRequired,
  onCardClick: PropTypes.func,
};

MarketCardUI.defaultProps = {
  onCardClick: undefined,
};

function comparePropsByKey(key, prev, next) {
  // react-slick carousel injects `style` object into each card
  // which is a new object on each rerender but contains same styles.
  // It triggers rerenders for each MarketCard.
  // Here we skip updates check for this prop, because MarketCard doesn't use it.
  return key === 'style' ? true : Object.is(prev[key], next[key]);
}

export const MarketCard = memo(MarketCardUI, (prev, next) => shallowEqual(prev, next, comparePropsByKey));
